import { hotTrends, stats, categories } from "@/mocks/site";
import { goSection } from "./bits";

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line text-primary-600",
  down: "ri-arrow-down-line text-foreground-300",
  flat: "ri-subtract-line text-foreground-300",
  new: "ri-arrow-up-line text-accent-600",
};

const rankStyle = ["text-primary-600", "text-accent-600", "text-secondary-500"];

export default function Hero({ onPickCategory }: { onPickCategory: (slug: string) => void }) {
  const pick = (slug: string) => {
    onPickCategory(slug);
    goSection("feed");
  };

  return (
    <section id="hero" className="relative scroll-mt-24 overflow-hidden">
      {/* decorative top rule */}
      <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-primary-600 via-accent-500 to-primary-600" />

      <div className="mx-auto max-w-7xl px-4 pb-14 pt-10 md:px-6 md:pb-16 md:pt-14">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
          {/* left headline */}
          <div className="lg:col-span-7">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50/70 px-3 py-1.5 text-xs font-medium text-primary-700">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500" />
              </span>
              每日大瓜 · 娱乐圈深度吃瓜现场
            </div>

            <h1 className="font-heading text-[34px] font-black leading-[1.15] tracking-tight text-foreground-950 md:text-[56px]">
              顶流塌房预警
              <br />
              <span className="text-primary-600">法制咖锤合集</span>
            </h1>

            <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-foreground-600 md:text-base">
              持续跟进<span className="font-medium text-foreground-900">税务问题爆料</span>、<span className="font-medium text-foreground-900">合同纠纷内幕</span>与<span className="font-medium text-foreground-900">行业潜规则曝光</span>，把零散的黑料预告整理成可核验的<strong className="text-primary-700">实锤证据链</strong>，从预告到落地全程追更。
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => goSection("feed")}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-semibold text-background-50 shadow-soft transition hover:bg-primary-700"
              >
                进入最新瓜闻
                <i className="ri-arrow-right-line" />
              </button>
              <button
                type="button"
                onClick={() => goSection("evidence")}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-focus-3-line text-primary-500" />
                看证据链深挖
              </button>
            </div>

            {/* quick chips */}
            <div className="mt-7 flex flex-wrap items-center gap-2">
              {categories.slice(0, 6).map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => pick(c.slug)}
                  className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-600 transition hover:bg-primary-50 hover:text-primary-700"
                >
                  #{c.name}
                </button>
              ))}
            </div>
          </div>

          {/* right hot list */}
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
              <div className="flex items-center justify-between border-b border-background-200 bg-background-100 px-5 py-3.5">
                <div className="flex items-center gap-2">
                  <i className="ri-fire-fill text-lg text-primary-500" />
                  <h2 className="font-heading text-base font-bold text-foreground-950">24h 吃瓜热榜</h2>
                </div>
                <span className="font-en text-[10px] uppercase tracking-widest text-foreground-400">Trending</span>
              </div>
              <ul className="divide-y divide-background-100">
                {hotTrends.map((t) => (
                  <li key={t.rank}>
                    <button
                      type="button"
                      onClick={() => pick(catNameToSlug(t.cat))}
                      className="group flex w-full items-center gap-3 px-5 py-3.5 text-left transition hover:bg-background-50"
                    >
                      <span
                        className={`font-heading text-xl font-black leading-none ${
                          rankStyle[t.rank - 1] ?? "text-foreground-300"
                        }`}
                      >
                        {String(t.rank).padStart(2, "0")}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                          {t.title}
                        </span>
                        <span className="mt-0.5 block text-xs text-foreground-400">
                          {t.cat} · {t.heat} 热度
                        </span>
                      </span>
                      <i className={`text-sm ${trendIcon[t.trend] ?? "ri-arrow-up-line text-foreground-300"}`} />
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => goSection("feed")}
                className="flex w-full items-center justify-center gap-2 border-t border-background-200 bg-background-50 py-3 text-xs font-medium text-primary-600 transition hover:text-primary-700"
              >
                查看更多热榜事件
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>

        {/* stats band */}
        <dl className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-background-200 bg-background-200 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="bg-background-100 px-6 py-5 text-center">
              <dd className="font-heading text-2xl font-black text-primary-600 md:text-3xl">{s.num}</dd>
              <dt className="mt-1 text-xs text-foreground-500 md:text-sm">{s.label}</dt>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function catNameToSlug(name: string): string {
  const hit = categories.find((c) => c.name === name);
  return hit ? hit.slug : "kua";
}